import { createFileRoute, Link } from "@tanstack/react-router";
import { Activity, Gauge, MemoryStick, Network, PoundSterling, Timer } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { Page, PageHeader } from "@/app/Page";
import { useEnvName, useProjectSlug } from "@/app/context";
import { useRegisterCommands } from "@/app/commands";
import {
  Badge,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  ExplainPanel,
  Money,
  Segmented,
  Select,
  ShowAsCode,
  StatusBadge,
  Switch,
} from "@/design";
import { cn } from "@/lib/cn";
import { docsUrl } from "@/lib/config";
import { formatNumber } from "@/lib/format";
import { TimeChart, type ChartSeries, type ChartThreshold } from "./-charts/TimeChart";
import { seriesToken } from "./-charts/colors";
import { fmtBytesPerSec, fmtCpu, fmtMb, fmtMs, fmtPerHour, fmtRps } from "./-metrics/format";
import { METRICS, RANGES, stats, sumSeries, useMetricsView, valuesOf, type RangeKey, type ServiceMetrics } from "./-metrics/useMetricsView";
import { minuteNow, prefetchInstances, prefetchMetrics } from "./-pending/prefetch";
import { MetricsPending, MetricsSkeleton } from "./-pending/skeletons";

interface MetricsSearch {
  range?: RangeKey;
  service?: string;
  cost?: boolean;
}

export const Route = createFileRoute("/$workspace/$project/metrics")({
  staticData: { title: "Metrics" },
  validateSearch: (s: Record<string, unknown>): MetricsSearch => ({
    ...(typeof s.range === "string" && RANGES.some((r) => r.value === s.range) ? { range: s.range as RangeKey } : {}),
    ...(typeof s.service === "string" && s.service ? { service: s.service } : {}),
    ...(s.cost === true || s.cost === "true" || s.cost === 1 ? { cost: true } : {}),
  }),
  // Start the charts' data while the screen's code downloads (and on hover). Same key as useMetricsView.
  loaderDeps: ({ search }) => ({ env: (search as { env?: string }).env ?? "production", range: search.range ?? "6h", service: search.service }),
  loader: ({ context, params, deps }) => {
    const now = minuteNow();
    const seconds = RANGES.find((r) => r.value === deps.range)?.seconds ?? 6 * 3600;
    prefetchInstances(context.queryClient, params.project, deps.env);
    prefetchMetrics(context.queryClient, params.project, {
      metric: METRICS,
      environment: deps.env,
      from: new Date(now - seconds * 1000).toISOString(),
      to: new Date(now).toISOString(),
      ...(deps.service ? { service: deps.service } : {}),
    });
  },
  pendingComponent: MetricsPending,
  pendingMs: 150,
  pendingMinMs: 200,
  component: MetricsScreen,
});

const SYNC = "guv-metrics";

function MetricsScreen() {
  const project = useProjectSlug();
  const env = useEnvName();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const range = search.range ?? "6h";
  const service = search.service;
  const showCost = !!search.cost;
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const { view, instances, isPending, error, refetch, isFetching } = useMetricsView(project, env, range, service);
  const rangeInfo = RANGES.find((r) => r.value === range)!;

  const setSearch = (patch: Partial<MetricsSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  useRegisterCommands(
    "Metrics",
    [
      ...RANGES.map((r) => ({ id: `metrics-range-${r.value}`, label: `Show metrics for ${r.long}`, keywords: ["range", r.value], run: () => setSearch({ range: r.value }) })),
      { id: "metrics-cost", label: showCost ? "Hide cost overlay" : "Show cost overlay", keywords: ["money", "£"], icon: <PoundSterling />, run: () => setSearch({ cost: showCost ? undefined : true }) },
    ],
    [showCost],
  );

  const serviceOptions = [
    { value: "__all", label: "All services" },
    ...instances.map((i) => ({ value: i.name, label: i.name, description: `${kindLabel(i.kind)} · ${i.status === "running" ? "live" : i.status.replace("_", " ")}` })),
  ];

  const visible = useMemo(() => (view?.services ?? []).filter((s) => !hidden.has(s.id)), [view, hidden]);
  const single = service ? view?.services[0] : visible.length === 1 ? visible[0] : undefined;

  return (
    <Page width="wide">
      <PageHeader
        title="Metrics"
        description="How hard your services are working, how fast they answer, and what that costs."
        actions={
          <>
            <ShowAsCode
              title="Query these metrics yourself"
              cli={`guv metrics --env ${env}${service ? ` --service ${service}` : ""} --since ${range} --metric cpu,memory,requests,latency_p95${showCost ? ",cost" : ""}`}
              apiCall={{ method: "GET", path: `/v1/projects/${project}/metrics?metric=cpu,memory,requests,latency_p95${showCost ? ",cost" : ""}&environment=${env}${service ? `&service=${service}` : ""}` }}
            />
          </>
        }
      />

      <div className="mb-6 flex flex-col gap-3 rounded-xl border border-border bg-surface p-3 shadow-xs sm:flex-row sm:flex-wrap sm:items-center">
        <div className="w-full sm:w-56">
          <Select
            aria-label="Service"
            size="sm"
            value={service ?? "__all"}
            onValueChange={(v) => {
              setHidden(new Set());
              setSearch({ service: v === "__all" ? undefined : v });
            }}
            options={serviceOptions}
          />
        </div>
        <Segmented
          aria-label="Time range"
          value={range}
          onValueChange={(v) => setSearch({ range: v })}
          options={RANGES.map((r) => ({ value: r.value, label: r.label, title: `Show ${r.long}` }))}
          className="self-start"
        />
        <div className="flex items-center gap-2.5 sm:ml-auto">
          <Switch size="sm" checked={showCost} onCheckedChange={(c) => setSearch({ cost: c ? true : undefined })} aria-label="Show cost overlay" />
          <span className="text-sm text-text">Cost overlay</span>
          <ExplainPanel title="What is the cost overlay?" learnMoreUrl={docsUrl("/pricing")} side="bottom">
            <p>
              Draws what you are paying, in <strong>pounds per hour</strong>, as a dashed line on every chart, so you can see how usage turns into money.
            </p>
            <p>You pay for what your app actually uses: £10 per vCPU-month and £5 per GB of memory per month, billed by the second.</p>
          </ExplainPanel>
          {isFetching && !isPending && <span className="sr-only" role="status">Refreshing metrics</span>}
        </div>
      </div>

      {error ? (
        <ErrorState error={error} onRetry={refetch} />
      ) : isPending || !view ? (
        <MetricsSkeleton />
      ) : view.services.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Activity />}
            title={`Nothing is running in ${env} yet`}
            description="Metrics appear here a minute or so after your first deploy. Sleeping and stopped services show as flat lines."
            actions={
              <Link to="/$workspace/$project/deployments" params={(p) => p as never} search={((s: Record<string, unknown>) => ({ env: s.env })) as never} className="text-sm font-medium text-accent-text hover:underline">
                Go to deployments
              </Link>
            }
          />
        </Card>
      ) : (
        <>
          {!service && view.services.length > 1 && (
            <div className="mb-5 flex flex-wrap items-center gap-2" role="group" aria-label="Show or hide services on the charts">
              <span className="mr-1 text-sm text-muted">Services</span>
              {view.services.map((s) => {
                const on = !hidden.has(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() =>
                      setHidden((h) => {
                        const n = new Set(h);
                        if (n.has(s.id)) n.delete(s.id);
                        else if (view.services.length - n.size > 1) n.add(s.id);
                        return n;
                      })
                    }
                    className={cn(
                      "inline-flex h-7 items-center gap-2 rounded-full border px-3 text-sm font-medium transition-colors",
                      on ? "border-border-strong bg-surface text-text shadow-xs" : "border-dashed border-border bg-transparent text-subtle line-through",
                    )}
                  >
                    <span className="size-2.5 rounded-sm" style={{ background: `var(${seriesToken(s.colorIndex)})`, opacity: on ? 1 : 0.35 }} aria-hidden="true" />
                    {s.name}
                  </button>
                );
              })}
            </div>
          )}

          <SummaryTiles services={visible} x={view.x} step={view.step} rangeLong={rangeInfo.long} single={single} />

          <div className="mt-6 grid gap-5 xl:grid-cols-2">
            <Charts services={visible} x={view.x} single={single} showCost={showCost} rangeLong={rangeInfo.long} />
          </div>

          {single && (
            <p className="mt-6 flex flex-wrap items-center gap-2 text-sm text-muted">
              <StatusBadge kind="service" status={single.status} />
              <span>
                Showing <strong className="font-medium text-text">{single.name}</strong> in {env}. Need detail?{" "}
                <Link
                  to="/$workspace/$project/logs"
                  params={(p) => p as never}
                  search={(s) => ({ ...s, q: `service:${single.name}` }) as never}
                  className="font-medium text-accent-text hover:underline"
                >
                  Open its logs
                </Link>
                .
              </span>
            </p>
          )}
        </>
      )}
    </Page>
  );
}

function kindLabel(kind: string | undefined): string {
  return kind === "worker" ? "Background worker" : kind === "cron" ? "Scheduled job" : kind === "private" ? "Private service" : "Web service";
}

/* ------------------------------------------------------------------ tiles */

function SummaryTiles({ services, x, step, rangeLong, single }: { services: ServiceMetrics[]; x: number[]; step: number; rangeLong: string; single?: ServiceMetrics }) {
  const n = x.length;
  const cpu = stats(sumSeries(services.map((s) => valuesOf(s, "cpu", n)), n));
  const mem = stats(sumSeries(services.map((s) => valuesOf(s, "memory", n)), n));
  const memLimit = services.reduce((a, s) => a + (s.series.memory?.limit ?? 0), 0);
  const cpuLimit = services.reduce((a, s) => a + (s.series.cpu?.limit ?? 0), 0);
  const web = services.filter((s) => s.kind === "web" || s.kind === undefined);
  const req = stats(sumSeries(web.map((s) => valuesOf(s, "requests", n)), n));
  const totalReq = req.sum * step;
  const busiest = single ?? [...web].sort((a, b) => stats(valuesOf(b, "requests", n)).avg - stats(valuesOf(a, "requests", n)).avg)[0];
  const p95 = busiest ? stats(valuesOf(busiest, "latency_p95", n)) : null;
  const p50 = busiest ? stats(valuesOf(busiest, "latency_p50", n)) : null;
  const cost = stats(sumSeries(services.map((s) => valuesOf(s, "cost", n)), n));
  const costInRange = cost.sum * (step / 3600);

  return (
    <Card className="grid grid-cols-2 divide-border overflow-hidden sm:grid-cols-3 lg:grid-cols-5 [&>*]:border-border">
      <Tile
        icon={<Gauge />}
        label="Average CPU"
        value={`${fmtCpu(cpu.avg)} vCPU`}
        hint={cpuLimit ? `${Math.round((cpu.avg / cpuLimit) * 100)}% of ${formatNumber(cpuLimit)} vCPU in limits` : `peak ${fmtCpu(cpu.max)}`}
        explain={
          <ExplainPanel title="Average CPU">
            <p>
              Processor time used over {rangeLong}, added up across {services.length === 1 ? "this service" : `${services.length} services`}. <strong>1 vCPU</strong> is one full processor core.
            </p>
            <p>The peak was {fmtCpu(cpu.max)} vCPU. Limits are a ceiling, not a charge: you only pay for what is used.</p>
          </ExplainPanel>
        }
      />
      <Tile
        icon={<MemoryStick />}
        label="Memory now"
        value={fmtMb(mem.last)}
        hint={memLimit ? `of ${fmtMb(memLimit)} in limits` : `peak ${fmtMb(mem.max)}`}
        explain={
          <ExplainPanel title="Memory now">
            <p>Working memory in use right now. If a service reaches its limit it restarts, so a comfortable gap is healthy.</p>
            <p>The highest point over {rangeLong} was {fmtMb(mem.max)}.</p>
          </ExplainPanel>
        }
      />
      <Tile
        icon={<Activity />}
        label="Requests"
        value={web.length ? compactCount(totalReq) : "None"}
        hint={web.length ? `peak ${fmtRps(req.max)}` : "No web services selected"}
        explain={
          <ExplainPanel title="Requests">
            <p>Every page load, API call and image fetched from your web services over {rangeLong}.</p>
            <p>Background workers and scheduled jobs do not receive requests, so they are left out.</p>
          </ExplainPanel>
        }
      />
      <Tile
        icon={<Timer />}
        label={busiest && !single && web.length > 1 ? `Response time (${busiest.name})` : "Response time"}
        value={p95 && p95.count ? fmtMs(p95.avg) : "–"}
        hint={p50 && p50.count ? `p95 · typical ${fmtMs(p50.avg)}` : "No requests to time"}
        explain={
          <ExplainPanel title="Response time (p95)">
            <p>
              <strong>p95</strong> means 95 in every 100 requests were answered faster than this. It is a fairer picture than the average, which hides the slow ones.
            </p>
            <p>“Typical” is the median (p50): half of requests were quicker.</p>
          </ExplainPanel>
        }
      />
      <Tile
        icon={<PoundSterling />}
        label="Cost in this range"
        value={<Money value={costInRange} />}
        hint={`about ${fmtPerHour(cost.avg).replace("/h", "")} an hour`}
        className="col-span-2 sm:col-span-1"
        explain={
          <ExplainPanel title="Cost in this range" learnMoreUrl={docsUrl("/pricing")}>
            <p>
              What these services cost over {rangeLong}, ex VAT, worked out from the CPU and memory they actually used. Databases and storage are on the Spend page.
            </p>
          </ExplainPanel>
        }
      />
    </Card>
  );
}

function compactCount(n: number): string {
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}m`;
  if (n >= 1e4) return `${Math.round(n / 1e3)}k`;
  return formatNumber(Math.round(n));
}

function Tile({ icon, label, value, hint, explain, className }: { icon: ReactNode; label: string; value: ReactNode; hint: ReactNode; explain?: ReactNode; className?: string }) {
  return (
    <div className={cn("min-w-0 border-b border-r p-4 sm:p-5", className)}>
      <div className="flex items-center gap-1.5 text-sm font-medium text-muted [&>svg]:size-3.5 [&>svg]:text-subtle">
        {icon}
        <span className="truncate">{label}</span>
        {explain}
      </div>
      <div className="mt-1.5 truncate font-display text-2xl font-semibold tabular text-text">{value}</div>
      <div className="mt-0.5 truncate text-sm text-muted">{hint}</div>
    </div>
  );
}

/* ----------------------------------------------------------------- charts */

function costSeries(services: ServiceMetrics[], n: number): ChartSeries {
  return {
    label: "Cost",
    values: sumSeries(services.map((s) => valuesOf(s, "cost", n)), n),
    color: "--text-muted",
    dash: [4, 4],
    scale: "cost",
    width: 1.5,
    format: fmtPerHour,
  };
}

function Charts({ services, x, single, showCost, rangeLong }: { services: ServiceMetrics[]; x: number[]; single?: ServiceMetrics; showCost: boolean; rangeLong: string }) {
  const n = x.length;
  const web = services.filter((s) => s.kind === "web" || s.kind === undefined);
  const cost = showCost ? [costSeries(services, n)] : [];
  const perService = (metric: Parameters<typeof valuesOf>[1], list = services, fill = list.length === 1): ChartSeries[] =>
    list.map((s) => ({ label: s.name, values: valuesOf(s, metric, n), color: seriesToken(s.colorIndex), fill }));

  const cpuLimit: ChartThreshold[] = single?.series.cpu?.limit ? [{ value: single.series.cpu.limit, label: `Limit ${fmtCpu(single.series.cpu.limit)} vCPU`, color: "--danger" }] : [];
  const memLimit: ChartThreshold[] = single?.series.memory?.limit ? [{ value: single.series.memory.limit, label: `Limit ${fmtMb(single.series.memory.limit)}`, color: "--danger" }] : [];

  // Plain-English one-liners under each chart.
  const busiestCpu = [...services].sort((a, b) => stats(valuesOf(b, "cpu", n)).avg - stats(valuesOf(a, "cpu", n)).avg)[0];
  const cpuStat = busiestCpu ? stats(valuesOf(busiestCpu, "cpu", n)) : null;
  const cpuLim = busiestCpu?.series.cpu?.limit;
  const biggestMem = [...services].sort((a, b) => stats(valuesOf(b, "memory", n)).max - stats(valuesOf(a, "memory", n)).max)[0];
  const memStat = biggestMem ? stats(valuesOf(biggestMem, "memory", n)) : null;
  const memLim = biggestMem?.series.memory?.limit;
  const errs = stats(sumSeries(web.map((s) => valuesOf(s, "errors_5xx", n)), n));
  const reqs = stats(sumSeries(web.map((s) => valuesOf(s, "requests", n)), n));
  const errRate = reqs.sum > 0 ? (errs.sum / reqs.sum) * 100 : 0;

  const latencySeries: ChartSeries[] =
    single && (single.kind === "web" || single.kind === undefined)
      ? [
          { label: "p50", values: valuesOf(single, "latency_p50", n), color: "--chart-2" },
          { label: "p95", values: valuesOf(single, "latency_p95", n), color: "--chart-1", fill: true },
          { label: "p99", values: valuesOf(single, "latency_p99", n), color: "--chart-4" },
        ]
      : web.map((s) => ({ label: `${s.name} p95`, values: valuesOf(s, "latency_p95", n), color: seriesToken(s.colorIndex) }));
  const latStat = single ? stats(valuesOf(single, "latency_p99", n)) : null;

  return (
    <>
      <ChartCard
        icon={<Gauge />}
        title="CPU"
        unit="vCPU"
        explain={
          <ExplainPanel title="CPU" learnMoreUrl={docsUrl("/metrics#cpu")}>
            <p>How much processor time each service used. 1 vCPU is one full core, so 0.25 means a quarter of one.</p>
            <p>If a service reaches its <strong>limit</strong> (the red dashed line) it slows down rather than crashing. You pay £10 per vCPU-month of actual use.</p>
          </ExplainPanel>
        }
        insight={
          busiestCpu && cpuStat
            ? `${busiestCpu.name} averaged ${fmtCpu(cpuStat.avg)} vCPU${cpuLim ? `, ${Math.round((cpuStat.avg / cpuLim) * 100)}% of its ${fmtCpu(cpuLim)} vCPU limit` : ""}, peaking at ${fmtCpu(cpuStat.max)}.`
            : undefined
        }
      >
        <TimeChart
          x={x}
          series={[...perService("cpu"), ...cost]}
          yFormat={fmtCpu}
          costFormat={fmtPerHour}
          thresholds={cpuLimit}
          syncKey={SYNC}
          label={`CPU use over ${rangeLong}`}
        />
      </ChartCard>

      <ChartCard
        icon={<MemoryStick />}
        title="Memory"
        unit="MB"
        explain={
          <ExplainPanel title="Memory" learnMoreUrl={docsUrl("/metrics#memory")}>
            <p>Working memory in use. When a service reaches its limit it is restarted, which briefly drops requests, so keep some headroom.</p>
            <p>You pay £5 per GB-month for what is actually used, not for the limit.</p>
          </ExplainPanel>
        }
        insight={
          biggestMem && memStat
            ? `${biggestMem.name} peaked at ${fmtMb(memStat.max)}${memLim ? `, leaving ${fmtMb(Math.max(0, memLim - memStat.max))} of headroom` : ""}.`
            : undefined
        }
      >
        <TimeChart x={x} series={[...perService("memory"), ...cost]} yFormat={fmtMb} costFormat={fmtPerHour} thresholds={memLimit} syncKey={SYNC} label={`Memory use over ${rangeLong}`} />
      </ChartCard>

      <ChartCard
        icon={<Activity />}
        title="Requests"
        unit="per second"
        explain={
          <ExplainPanel title="Requests">
            <p>Requests per second reaching your web services. The red line counts <strong>server errors</strong> (5xx): requests your app failed to answer properly.</p>
            <p>4xx responses, such as “not found”, are usually visitors asking for the wrong thing and are not counted as errors.</p>
          </ExplainPanel>
        }
        insight={web.length ? (errs.sum > 0 ? `${errRate < 0.01 ? "Fewer than 0.01" : errRate.toFixed(2)}% of requests failed with a server error.` : "No server errors in this range.") : undefined}
      >
        {web.length ? (
          <TimeChart
            x={x}
            series={[
              ...perService("requests", web),
              { label: "Server errors", values: sumSeries(web.map((s) => valuesOf(s, "errors_5xx", n)), n), color: "--danger", width: 1.25, format: fmtRps },
              ...cost,
            ]}
            yFormat={fmtRps}
            costFormat={fmtPerHour}
            syncKey={SYNC}
            label={`Requests per second over ${rangeLong}`}
          />
        ) : (
          <NoTraffic />
        )}
      </ChartCard>

      <ChartCard
        icon={<Timer />}
        title="Response time"
        unit={single ? "p50, p95, p99" : "p95"}
        explain={
          <ExplainPanel title="Response time">
            <p>
              How long requests take. <strong>p50</strong> is a typical request. <strong>p95</strong>: 95 in 100 were faster. <strong>p99</strong> is the slowest 1 in 100, which is what your unluckiest
              visitors feel.
            </p>
            <p>Pick a single service above to see all three lines.</p>
          </ExplainPanel>
        }
        insight={latStat && latStat.count ? `The slowest 1 in 100 requests took about ${fmtMs(latStat.avg)} on average.` : undefined}
      >
        {latencySeries.length ? (
          <TimeChart x={x} series={[...latencySeries, ...cost]} yFormat={fmtMs} costFormat={fmtPerHour} syncKey={SYNC} label={`Response times over ${rangeLong}`} />
        ) : (
          <NoTraffic />
        )}
      </ChartCard>

      <ChartCard
        icon={<Network />}
        title="Network"
        unit={single ? "in and out" : "data out"}
        className="xl:col-span-2"
        explain={
          <ExplainPanel title="Network" learnMoreUrl={docsUrl("/pricing#egress")}>
            <p>Data flowing in to and out of your services. Traffic between your own services on the private network is free and not shown.</p>
            <p>The first 100 GB out each month is free, then £0.02 per GB.</p>
          </ExplainPanel>
        }
      >
        <TimeChart
          x={x}
          height={180}
          series={[
            ...(single
              ? [
                  { label: "In", values: valuesOf(single, "network_in", n), color: "--chart-2" },
                  { label: "Out", values: valuesOf(single, "network_out", n), color: "--chart-1", fill: true },
                ]
              : services.map((s) => ({ label: `${s.name} out`, values: valuesOf(s, "network_out", n), color: seriesToken(s.colorIndex) }))),
            ...cost,
          ]}
          yFormat={fmtBytesPerSec}
          costFormat={fmtPerHour}
          syncKey={SYNC}
          label={`Network traffic over ${rangeLong}`}
        />
      </ChartCard>
    </>
  );
}

function NoTraffic() {
  return (
    <EmptyState
      size="sm"
      icon={<Activity />}
      title="No web traffic here"
      description="Background workers and scheduled jobs do not receive requests. Choose a web service to see this chart."
    />
  );
}

function ChartCard({
  icon,
  title,
  unit,
  explain,
  insight,
  children,
  className,
}: {
  icon: ReactNode;
  title: string;
  unit: string;
  explain: ReactNode;
  insight?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Card className={cn("min-w-0", className)}>
      <CardHeader
        icon={icon}
        title={
          <span className="inline-flex items-center gap-1.5">
            {title}
            {explain}
          </span>
        }
        actions={<Badge variant="outline" size="sm">{unit}</Badge>}
        className="pb-1"
      />
      <div className="px-4 pb-4 sm:px-5">
        {children}
        {insight && <p className="mt-2 text-sm text-muted">{insight}</p>}
      </div>
    </Card>
  );
}

